import React, { useState } from 'react';
import styled from 'styled-components';

const PageHeader = styled.div`
  padding: 8rem 0 4rem;
  background-color: var(--color-primary-dark);
  color: var(--color-light);
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to right bottom, var(--color-primary-dark) 49%, var(--color-white) 50%);
  }
`;

const PageHeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.9;
  max-width: 700px;
`;

const ContactSection = styled.section`
  padding: 6rem 0;
`;

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div``;

const ContactTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ContactText = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  margin-bottom: 2.5rem;
`;

const ContactMethods = styled.div`
  margin-bottom: 3rem;
`;

const ContactMethod = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
`;

const MethodIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: var(--color-accent);
  color: var(--color-white);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-right: 1rem;
`;

const MethodDetails = styled.div``;

const MethodTitle = styled.h3`
  font-size: 1.2rem;
  margin-bottom: 0.25rem;
`;

const MethodValue = styled.p`
  font-size: 1.1rem;
  color: var(--color-accent);
`;

const ContactSSO = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  background-color: var(--color-gray);
  border-radius: 8px;
`;

const SSOTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const SSOText = styled.p`
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const SSOButton = styled.a`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: var(--color-accent);
  color: var(--color-white);
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-primary-dark);
    color: var(--color-white);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ContactForm = styled.div`
  background-color: var(--color-white);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const FormLabel = styled.label`
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent);
  }
`;

const FormSubmitButton = styled.button`
  display: inline-block;
  padding: 0.8rem 2rem;
  background: var(--color-accent);
  color: var(--color-white);
  font-weight: 600;
  border-radius: 4px;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: var(--color-primary-dark);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FormSuccess = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const FormError = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
`;

const MapSection = styled.section`
  padding: 0 0 6rem;
`;

const MapContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const MapTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const MapFrame = styled.div`
  width: 100%;
  height: 450px;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [formStatus, setFormStatus] = useState({
    submitted: false,
    success: false,
    error: null
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission with a timeout
    setTimeout(() => {
      setFormStatus({
        submitted: true,
        success: true,
        error: null
      });
      setIsSubmitting(false);
      
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    }, 1500);
  };
  
  return (
    <>
      <PageHeader>
        <PageHeaderContent>
          <PageTitle>Contact Us</PageTitle>
          <PageSubtitle>
            Have a question or want to discuss how we can help with your technology challenges? Reach out to us.
          </PageSubtitle>
        </PageHeaderContent>
      </PageHeader>
      
      <ContactSection>
        <ContactContainer>
          <ContactInfo>
            <ContactTitle>Get in Touch</ContactTitle>
            <ContactText>
              We'd love to hear from you. Whether you have a question about our services, want to discuss a project, or just want to say hello, we're here to help.
            </ContactText>
            
            <ContactMethods>
              <ContactMethod>
                <MethodIcon>
                  <i className="fas fa-envelope"></i>
                </MethodIcon>
                <MethodDetails>
                  <MethodTitle>Email Us</MethodTitle>
                  <MethodValue>info@kriegerlabs.net</MethodValue>
                </MethodDetails>
              </ContactMethod>
              
              <ContactMethod>
                <MethodIcon>
                  <i className="fas fa-map-marker-alt"></i>
                </MethodIcon>
                <MethodDetails>
                  <MethodTitle>Location</MethodTitle>
                  <MethodValue>San Francisco, CA</MethodValue>
                </MethodDetails>
              </ContactMethod>
              
              <ContactMethod>
                <MethodIcon>
                  <i className="fas fa-clock"></i>
                </MethodIcon>
                <MethodDetails>
                  <MethodTitle>Working Hours</MethodTitle>
                  <MethodValue>Monday - Friday: 9:00 AM - 6:00 PM PST</MethodValue>
                </MethodDetails>
              </ContactMethod>
            </ContactMethods>
            
            <ContactSSO>
              <SSOTitle>Client Portal</SSOTitle>
              <SSOText>
                Existing clients can access our secure portal to view project status, documentation, and collaborate with our team.
              </SSOText>
              <SSOButton 
                href="https://dev-84918634.okta.com/app/zendesk/exkt89ftiX6QbfOxz5d6/sso/saml"
                target="_blank"
                rel="noopener noreferrer"
              >
                SSO Sign In
              </SSOButton>
            </ContactSSO>
          </ContactInfo>
          
          <ContactForm>
            <FormTitle>Send Us a Message</FormTitle>
            
            {formStatus.submitted && formStatus.success && (
              <FormSuccess>
                Thank you for your message! We'll get back to you shortly.
              </FormSuccess>
            )}
            
            {formStatus.submitted && !formStatus.success && (
              <FormError>
                {formStatus.error || "There was an error sending your message. Please try again."}
              </FormError>
            )}
            
            <form onSubmit={handleSubmit}>
              <FormGroup>
                <FormLabel htmlFor="name">Your Name</FormLabel>
                <FormInput 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleInputChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="email">Email Address</FormLabel>
                <FormInput 
                  type="email" 
                  id="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="subject">Subject</FormLabel>
                <FormInput 
                  type="text" 
                  id="subject" 
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required 
                />
              </FormGroup>
              
              <FormGroup>
                <FormLabel htmlFor="message">Your Message</FormLabel>
                <FormTextarea 
                  id="message" 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required 
                />
              </FormGroup>
              
              <FormSubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </FormSubmitButton>
            </form>
          </ContactForm>
        </ContactContainer>
      </ContactSection>
      
      <MapSection>
        <MapContainer>
          <MapTitle>Find Us</MapTitle>
          <MapFrame>
            <iframe 
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d50470.25859091097!2d-122.48214936953499!3d37.77492951316926!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80859a6d00690021%3A0x4a501367f076adff!2sSan%20Francisco%2C%20CA!5e0!3m2!1sen!2sus!4v1633824028018!5m2!1sen!2sus" 
              width="100%" 
              height="100%" 
              style={{ border: 0 }}
              allowFullScreen="" 
              loading="lazy"
              title="Kriegerlabs location map"
            ></iframe>
          </MapFrame>
        </MapContainer>
      </MapSection>
    </>
  );
};

export default ContactPage;
